import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Funnel, FunnelStage } from '../../../types';
import { mapStageRow, STAGE_SELECT } from './contactPanelDeals.helpers';

interface UseContactPanelDealStagesParams {
    funnelStages: FunnelStage[];
    selectedFunnelId: string | null;
    funnels: Funnel[];
    organizationId?: string;
}

export function useContactPanelDealStages({
    funnelStages,
    selectedFunnelId,
    funnels,
    organizationId
}: UseContactPanelDealStagesParams): FunnelStage[] {
    const [availableStages, setAvailableStages] = useState<FunnelStage[]>(funnelStages);

    useEffect(() => {
        const effectiveFunnelId = selectedFunnelId ?? funnels[0]?.id ?? null;

        if (funnelStages.length > 0) {
            const aligned = effectiveFunnelId
                ? funnelStages.filter(stage => !stage.funnelId || stage.funnelId === effectiveFunnelId)
                : funnelStages;
            setAvailableStages(aligned.length > 0 ? aligned : funnelStages);
            return;
        }

        const fetchStages = async () => {
            if (!organizationId) {
                setAvailableStages([]);
                return;
            }

            let funnelId = effectiveFunnelId;
            if (!funnelId) {
                const { data: funnel } = await supabase
                    .from('funnels')
                    .select('id')
                    .eq('organization_id', organizationId)
                    .order('created_at', { ascending: true })
                    .limit(1)
                    .maybeSingle();
                funnelId = funnel?.id ?? null;
            }

            if (!funnelId) {
                setAvailableStages([]);
                return;
            }

            const { data, error } = await supabase
                .from('funnel_stages')
                .select(STAGE_SELECT)
                .eq('organization_id', organizationId)
                .eq('funnel_id', funnelId)
                .order('order', { ascending: true });

            if (error) {
                console.error('[useContactPanelDeals] Error fetching stages for panel:', error);
                setAvailableStages([]);
                return;
            }

            setAvailableStages((data || []).map(mapStageRow));
        };

        fetchStages();
    }, [funnelStages, organizationId, selectedFunnelId, funnels]);

    return availableStages;
}
